import { useState } from 'react'

function sanitizeInput(input) {
  // Remove script tags and trim whitespace
  return input.replace(/<script.*?>.*?<\/script>/gi, '').trim();
}

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // honeypot
  })
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateName = (name) => {
    return /^[a-zA-Z\s'-]{2,50}$/.test(name);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('');
    // Honeypot: if website field is filled, it's a bot
    if (formData.website) {
      setError('Spam detected.');
      return;
    }
    if (!validateName(formData.name)) {
      setError('Please enter a valid name (letters, spaces, apostrophes, 2-50 chars).');
      return;
    }
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!formData.subject) {
      setError('Please select a subject.');
      return;
    }
    if (formData.message.length < 10 || formData.message.length > 1000) {
      setError('Message must be between 10 and 1000 characters.');
      return;
    }
    // Sanitize message
    const sanitizedMessage = sanitizeInput(formData.message);
    // Create mailto link with sanitized data
    const mailtoLink = `mailto:tamarvalleybusinessassociation@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${sanitizedMessage}`)}`
    window.location.href = mailtoLink
  }

  return (
    <div className="py-20">
      <div className="container-width section-padding">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Get in <span className="text-secondary">Touch</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Ready to join our vibrant business community? Have questions about membership or upcoming events? 
            We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-8">Contact Information</h2>
            
            <div className="space-y-8">
              {/* Email */}
              <div className="flex items-start space-x-4">
                <a 
                  href="mailto:tamarvalleybusinessassociation@gmail.com"
                  className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0 hover:bg-primary-500 transition-colors duration-200"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </a>
                <div className="min-w-0 flex-1 break-words">
                  <h3 className="text-xl font-semibold text-white mb-2">Email</h3>
                  <a 
                    href="mailto:tamarvalleybusinessassociation@gmail.com"
                    className="text-primary-300 hover:text-primary-200 transition-colors duration-200 break-all"
                  >
                    tamarvalleybusinessassociation@gmail.com
                  </a>
                  <p className="text-gray-200 mt-1">
                    For membership inquiries, partnership opportunities, and general information
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start space-x-4">
                <a 
                  href="https://maps.app.goo.gl/em4EQaXQv2phzruF8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0 hover:bg-primary-500 transition-colors duration-200"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </a>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Location</h3>
                  <a 
                    href="https://maps.app.goo.gl/em4EQaXQv2phzruF8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-300 hover:text-primary-200 transition-colors duration-200"
                  >
                    Beaconsfield, Tasmania
                  </a>
                  <p className="text-gray-300 mt-1">
                    Visit us at the Exeter Markets for face-to-face meetings and networking opportunities
                  </p>
                </div>
              </div>

              {/* Social Media */}
              <div className="flex items-start space-x-4">
                <a 
                  href="https://www.facebook.com/p/Tamar-Valley-Business-Association-100070927818616/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0 hover:bg-primary-500 transition-colors duration-200"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Social Media</h3>
                  <a 
                    href="https://www.facebook.com/p/Tamar-Valley-Business-Association-100070927818616/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-300 hover:text-primary-200 transition-colors duration-200"
                  >
                    Follow us on Facebook
                  </a>
                  <p className="text-gray-200 mt-1">
                    Stay updated with our latest events and community news
                  </p>
                </div>
              </div>
            </div>

            {/* Exeter Markets Info */}
            <div className="p-6 mt-8">
              <h3 className="text-xl font-semibold text-white mb-3">Visit Us at Exeter Markets</h3>
              <p className="text-gray-200 leading-relaxed">
                The Exeter markets serve as a vibrant hub where you can meet our team, learn about membership 
                benefits, and connect with other local business owners. Come and experience our community firsthand!
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-8">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div style={{ display: 'none' }}>
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  autoComplete="off"
                  tabIndex="-1"
                />
              </div>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 text-white placeholder-gray-300"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 text-white placeholder-gray-300"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 text-white [&>option]:bg-gray-800 [&>option]:text-white"
                >
                  <option value="" className="bg-gray-800 text-white">Select a subject</option>
                  <option value="Membership Inquiry" className="bg-gray-800 text-white">Membership Inquiry</option>
                  <option value="Partnership Opportunity" className="bg-gray-800 text-white">Partnership Opportunity</option>
                  <option value="Event Information" className="bg-gray-800 text-white">Event Information</option>
                  <option value="General Question" className="bg-gray-800 text-white">General Question</option>
                  <option value="Other" className="bg-gray-800 text-white">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 resize-vertical text-white placeholder-gray-300"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              {error && <div className="text-red-300 text-sm text-center">{error}</div>}

              <button
                type="submit"
                className="w-full btn-primary py-4 text-lg"
              >
                Send Message
              </button>

              <p className="text-sm text-gray-200 text-center">
                * Required fields. This form will open your email client with the message pre-filled.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact 